/**
 * Pure builder + validator turning Print-page form state into a
 * `PrintLabelRequest`. Kept separate from the page so the validation rules can
 * be unit-tested without rendering.
 */
import type { CustomerIndexItemDto, PrintLabelRequest } from '@techgitter/contracts';
import {
  PIN_LABEL_DOCUMENT,
  PASSWORD_LABEL_DOCUMENT,
  CUSTOMER_LABEL_DOCUMENT,
  type LabelCatalogEntry,
} from '../config/labelCatalog';

export type LabelKind = 'pin' | 'password' | 'preformatted' | 'customer';

export interface BuildLabelRequestInput {
  kind: LabelKind;
  /** Free-text value for the `pin` kind. */
  pin?: string;
  /** Free-text value for the `password` kind. */
  password?: string;
  /** Selected catalog entry for the `preformatted` kind. */
  preformatted?: LabelCatalogEntry | null;
  /** Field values keyed by `LabelField.name` for the `preformatted` kind. */
  fieldValues?: Record<string, string>;
  /** Picked customer for the `customer` kind. */
  customer?: CustomerIndexItemDto | null;
  /** `customer` kind: include the customer's mobile (checkbox; default on). */
  customerShowMobile?: boolean;
  /** `customer` kind: include the customer's email (checkbox; default on). */
  customerShowEmail?: boolean;
  /** `customer` kind: optional freetext note printed beneath the contacts. */
  customerNote?: string;
  /** Copies to print; coerced to an integer >= 1. */
  copies?: number;
  /**
   * Printer lane to address. Omitted/null lets sm4rt-api resolve its configured
   * default site + role — the behaviour before the Print page offered a picker.
   * Both halves travel together: `role` alone would be resolved against the
   * API's default site, which is the wrong printer if the lane belongs to
   * another one.
   */
  printer?: { siteId: string; role: string } | null;
}

export type BuildLabelResult =
  | { ok: true; request: PrintLabelRequest }
  | { ok: false; error: string };

/** Copies coerced to a whole number, floored at 1. */
export function normalizeCopies(copies: number | undefined): number {
  if (!Number.isFinite(copies as number)) return 1;
  return Math.max(1, Math.floor(copies as number));
}

export function buildPrintLabelRequest(input: BuildLabelRequestInput): BuildLabelResult {
  const copies = normalizeCopies(input.copies);

  const printer = input.printer ?? null;

  const withCommon = (document: string, data: Record<string, string>): PrintLabelRequest => ({
    document,
    data,
    media: '90mm',
    copies,
    // Keys are added only when a lane was picked, so an unpicked print sends
    // exactly the body it always did and the API applies its own defaults.
    ...(printer ? { siteId: printer.siteId, role: printer.role } : {}),
  });

  if (input.kind === 'pin') {
    const pin = (input.pin ?? '').trim();
    if (!pin) return { ok: false, error: 'Enter a PIN to print.' };
    return { ok: true, request: withCommon(PIN_LABEL_DOCUMENT, { pin }) };
  }

  if (input.kind === 'password') {
    const password = (input.password ?? '').trim();
    if (!password) return { ok: false, error: 'Enter a password to print.' };
    return { ok: true, request: withCommon(PASSWORD_LABEL_DOCUMENT, { password }) };
  }

  if (input.kind === 'customer') {
    const customer = input.customer;
    if (!customer) return { ok: false, error: 'Choose a customer to print.' };
    const customerName = (
      customer.businessName || customer.displayName || customer.fullName || ''
    ).trim();
    if (!customerName) return { ok: false, error: 'Selected customer has no name.' };
    // The QR deep-links to the /customer/:id route, opening the customer page
    // in whatever environment the app is served from. Prefer the RepairShopr
    // id (a short, scan-friendly URL); fall back to the Dataverse GUID. The
    // route resolves either.
    const customerLinkId = customer.repairShoprCustomerId || customer.dataverseCustomerId;
    const data: Record<string, string> = {
      customerName,
      qrUrl: `${window.location.origin}/customer/${customerLinkId}`,
    };
    // Optional contact fields — included only when the field's checkbox is
    // ticked (default on) and the record has a value. Unticking simply omits
    // the value, which is how the template hides the field (it renders only
    // what's present). Matches the preformatted branch's omit-empty rule.
    if (input.customerShowMobile ?? true) {
      const mobile = (customer.mobile || customer.phone || '').trim();
      if (mobile) data.mobile = mobile;
    }
    if (input.customerShowEmail ?? true) {
      const email = (customer.email || '').trim();
      if (email) data.email = email;
    }
    // Optional freetext note, rendered wrapped beneath the contacts.
    const note = (input.customerNote ?? '').trim();
    if (note) data.note = note;
    return { ok: true, request: withCommon(CUSTOMER_LABEL_DOCUMENT, data) };
  }

  // preformatted
  const entry = input.preformatted;
  if (!entry) return { ok: false, error: 'Choose a label to print.' };

  const values = input.fieldValues ?? {};
  const data: Record<string, string> = {};
  for (const field of entry.fields) {
    const value = (values[field.name] ?? '').trim();
    if (field.required && !value) {
      return { ok: false, error: `${field.label} is required.` };
    }
    if (value) data[field.name] = value;
  }
  return { ok: true, request: withCommon(entry.document, data) };
}
